import { Controller } from "@hotwired/stimulus"

// "We might already have this" — shown while the name is still being typed.
//
// The duplicate check already existed on SUBMIT, which is the worst moment
// to find out: the nutrition panel is filled in, and being told to throw it
// away is a thing people ignore. Here it costs nothing to notice.
//
// DELIBERATELY NOT A BLOCK. It suggests, and the form still submits — the
// matching is a name similarity, so it is sometimes wrong, and a check that
// is sometimes wrong must never be the thing standing between someone and
// their own data. The submit-time confirmation stays as the backstop.
export default class extends Controller {
  static targets = ["name", "brand", "results", "list"]
  // choose: what a match DOES when tapped.
  //
  //   "link"   — go to the food's page. The full form, where you are
  //              managing the library and want to look at the thing.
  //   "adopt"  — hand it back to whatever asked for a new food and close.
  //              The inline form, where you were part way through a recipe
  //              or a meal and a match means "you do not need to add this".
  //
  // A link is wrong inline: it abandons the recipe you are writing to show
  // you a page you did not ask for.
  static values = {
    url: String,
    id: String,
    minLength: { type: Number, default: 3 },
    choose: { type: String, default: "link" }
  }

  disconnect() {
    clearTimeout(this.timer)
    this.controller?.abort()
  }

  look() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetch(), 300)
  }

  async fetch() {
    const name = this.nameTarget.value.trim()

    if (name.length < this.minLengthValue) return this.hide()

    // One request at a time. Without this, a slow early response can land
    // after a fast later one and leave results for a name you have already
    // typed past.
    this.controller?.abort()
    this.controller = new AbortController()

    const params = new URLSearchParams({ q: name })
    if (this.hasBrandTarget && this.brandTarget.value.trim()) {
      params.set("brand", this.brandTarget.value.trim())
    }
    if (this.idValue) params.set("id", this.idValue)

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" },
        signal: this.controller.signal,
      })
      if (!response.ok) return this.hide()

      this.render(await response.json())
    } catch (error) {
      // An aborted request is the expected case, not a failure. Anything
      // else: say nothing rather than showing an error for a suggestion
      // nobody asked for.
      if (error.name !== "AbortError") this.hide()
    }
  }

  render(foods) {
    if (!foods.length) return this.hide()

    this.listTarget.replaceChildren(...foods.map((food) => this.row(food)))
    this.resultsTarget.hidden = false
  }

  row(food) {
    const link = this.chooseValue === "adopt"
      ? document.createElement("button")
      : document.createElement("a")

    if (this.chooseValue === "adopt") {
      link.type = "button"
      link.addEventListener("click", () => this.adopt(food))
    } else {
      link.href = food.path
    }

    // A ROW ON THE SUNKEN PANEL NEEDS ITS OWN GROUND.
    //
    // .combobox-option hovers to --color-sunken, which is exactly what this
    // panel is filled with — so the hover painted the row the colour it was
    // already sitting on and nothing happened. On a dropdown, where the
    // panel is --color-surface, the same class works perfectly.
    //
    // The rows take the surface colour and a border, so hovering has
    // somewhere to go and each row reads as a thing rather than a line of
    // text on a block.
    link.className =
      "combobox-option flex w-full items-baseline justify-between gap-3 " +
      "rounded-lg border border-line bg-surface px-3 py-2 text-left text-sm " +
      "text-ink no-underline transition-colors hover:border-ink-faint"

    const label = document.createElement("span")
    label.textContent = food.name

    if (food.brand) {
      const brand = document.createElement("span")
      brand.className = "ml-1.5 text-xs text-ink-faint"
      brand.textContent = food.brand
      label.append(brand)
    }

    // WHOSE IT IS, and "added by @cody" rather than a bare @cody: the
    // phrase is what makes it attribution rather than a second brand. A
    // bare "Cody" beside a food name reads as one — which is the field
    // immediately to its left.
    //
    // Brand-deep, not grey: a food somebody made is the strongest reason to
    // use theirs rather than add another, so it is the one thing on the row
    // that is coloured.
    if (food.mine || food.owner) {
      const owner = document.createElement("span")
      owner.className = "ml-1.5 text-xs text-brand-deep"
      owner.textContent = food.mine ? "yours" : `added by @${food.owner}`
      label.append(owner)
    }

    const energy = document.createElement("span")
    energy.className = "tabular shrink-0 text-xs text-ink-faint"
    energy.textContent = `${food.kcal} kcal / 100${food.unit}`

    const right = document.createElement("span")
    right.className = "flex shrink-0 items-center gap-1.5"
    right.append(energy)

    // A CARET, because nothing else says the row does anything. In the full
    // form these are links and the underline says so; here they are buttons
    // that put the food where you were, and an unmarked row of text reads as
    // a list you are being shown rather than one you can act on.
    if (this.chooseValue === "adopt") {
      right.append(this.caret())
    }

    link.append(label, right)
    return link
  }

  // Lucide `chevron-right`, the same glyph the modal header's Back mirrors.
  caret() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("width", "14")
    svg.setAttribute("height", "14")
    svg.setAttribute("viewBox", "0 0 24 24")
    svg.setAttribute("fill", "none")
    svg.setAttribute("stroke", "currentColor")
    svg.setAttribute("stroke-width", "2.5")
    svg.setAttribute("stroke-linecap", "round")
    svg.setAttribute("stroke-linejoin", "round")
    svg.setAttribute("aria-hidden", "true")
    svg.classList.add("shrink-0", "text-ink-faint")

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("d", "m9 18 6-6-6-6")
    svg.append(path)

    return svg
  }

  // A match chosen is a create abandoned — the whole point of showing them.
  // What "chosen" MEANS differs per caller and cannot be generalised here:
  // the recipe editor writes the food into the row it came from, the picker
  // navigates to that food's amount step. So this announces the choice and
  // the caller decides.
  adopt(food) {
    this.dispatch("chosen", { detail: { food }, prefix: "similar-foods" })
  }

  hide() {
    this.resultsTarget.hidden = true
    this.listTarget.replaceChildren()
  }
}
